import type { ChangePoint, ChangePointEntry } from "@repo/api";
import type { DefinitionRevisionChange, DefinitionVersionHistoryEntry } from "@repo/data-store";
import { Timeline, TimelineIndicator, TimelineItem, TimelineSeparator } from "@repo/ui/components/reui/timeline";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@repo/ui/components/ui/collapsible";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@repo/ui/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/components/ui/tooltip";
import { cn } from "@repo/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { ChevronDown, CirclePlus, GitBranch, History, type LucideIcon, RotateCcw, Target } from "lucide-react";
import { useState } from "react";
import { formatDate } from "@/shared/lib/date-formats";
import { useRevisionChanges } from "../../../hooks/use-definition-as-of";
import { useCatalogDefinition } from "./catalog-definition-context";

/**
 * Opens the domain revision lineage for the definition rendered by this shell.
 * Rendered by the toolbar, not by the page — the page's only say is whether it
 * gave `Root` a `versions` array.
 */
function VersionHistory() {
  const [open, setOpen] = useState(false);
  const { state } = useCatalogDefinition("VersionHistory");
  const { asOf } = state;
  const versions = state.versions ?? [];
  // A never-revised lineage shows the same version number at every instant, so
  // the version alone cannot say whether this is the present. `asOf` can.
  const isLatest = !asOf && showingLatest(state.shell.version, versions);
  // On a past view the instant rides along with the version, because the version
  // alone would read as the present on any lineage that was never revised.
  const label = isLatest
    ? "Version: Latest"
    : asOf
      ? `Version: ${state.shell.version} · ${formatDate(asOf)}`
      : `Version: ${state.shell.version}`;

  return (
    <>
      {/* Two semantic tokens, both theme-aware with checked foregrounds: `info`
          is synapse-blue, the brand's second blue, for the live version; a past
          version takes `warning` amber and the icon, because the page is then
          showing a record rather than the definition in force, and mistaking one
          for the other is the error worth shouting about. Not `destructive` —
          nothing is broken, it just is not current. */}
      <Badge
        className={cn(
          "cursor-pointer",
          isLatest ? "bg-info text-info-foreground" : "bg-warning text-warning-foreground",
        )}
        aria-label={`${label} — open version history`}
        render={<button type="button" onClick={() => setOpen(true)} />}
      >
        {!isLatest && <History aria-hidden="true" />}
        {label}
      </Badge>
      {/* Getting back to the present is a one-click exit, not something to go
          looking for: the badge opens history, this leaves the past. */}
      {!isLatest && (
        <Button
          variant="outline"
          size="xs"
          render={
            <Link
              to="."
              search={(previous) => {
                const { asOf: _cleared, ...rest } = previous as Record<string, unknown>;
                return rest;
              }}
            />
          }
        >
          <RotateCcw aria-hidden="true" />
          Back to latest
        </Button>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="overflow-y-auto sm:!max-w-xl">
          <VersionHistorySheet
            versions={versions}
            changePoints={state.changePoints ?? []}
            title={state.shell.title}
            shownVersion={state.shell.version}
            asOf={asOf}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}

/**
 * One chronology, two kinds of entry.
 *
 * A version is a revision OF this definition. A change point is an instant at
 * which the graph under it resolves differently — which includes every revision
 * of a CHILD, and those are not versions of anything the version history knows
 * about. On a composed kind most entries are of the second sort, and merging
 * them is the only way the sheet tells the truth about "what changed when".
 *
 * A change point whose instant is already a version's authoring date is dropped:
 * it IS that version, and listing it twice would read as two events.
 */
function mergeHistory(
  versions: readonly DefinitionVersionHistoryEntry[],
  changePoints: readonly ChangePoint[],
): HistoryEntry[] {
  const versionInstants = new Set(versions.flatMap((entry) => (entry.authoredAt ? [entry.authoredAt] : [])));
  const childOnly: HistoryEntry[] = changePoints
    .filter((point) => !versionInstants.has(point.instant))
    .map((point) => ({ kind: "changePoint" as const, point }));
  const asVersions: HistoryEntry[] = versions.map((version) => ({ kind: "version" as const, version }));

  // Undated versions sort last rather than being dropped: the sheet still owes
  // the reader a row for a revision it cannot place in time.
  return [...asVersions, ...childOnly].sort((a, b) => (instantOf(b) ?? "").localeCompare(instantOf(a) ?? ""));
}

type HistoryEntry =
  | { kind: "version"; version: DefinitionVersionHistoryEntry }
  | { kind: "changePoint"; point: ChangePoint };

function instantOf(entry: HistoryEntry): string | undefined {
  return entry.kind === "version" ? entry.version.authoredAt : entry.point.instant;
}

function VersionHistorySheet({
  versions,
  changePoints,
  title,
  shownVersion,
  asOf,
}: {
  versions: readonly DefinitionVersionHistoryEntry[];
  changePoints: readonly ChangePoint[];
  title: string;
  shownVersion?: string;
  asOf?: string;
}) {
  const latest = versions.find((entry) => entry.current)?.version;
  // The version the page is showing, which is the latest until a route says
  // otherwise. "Active" and "latest" are the same thing only until you go back.
  const active = shownVersion ?? latest;
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => (active ? { [active]: true } : {}));
  const allExpanded = versions.length > 0 && versions.every((entry) => expanded[entry.version]);
  const entries = mergeHistory(versions, changePoints);
  const others = entries.length - versions.length;

  return (
    <>
      <SheetHeader>
        <SheetTitle>Version history</SheetTitle>
        <SheetDescription>
          {title} · {versions.length} {versions.length === 1 ? "version" : "versions"}
          {others > 0 && ` · ${others} other ${others === 1 ? "change" : "changes"} underneath`}
        </SheetDescription>
        {versions.length > 0 && (
          <div className="mt-3 flex items-center justify-between gap-3">
            <Legend />
            <button
              type="button"
              className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() =>
                setExpanded(allExpanded ? {} : Object.fromEntries(versions.map((entry) => [entry.version, true])))
              }
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </button>
          </div>
        )}
      </SheetHeader>

      <div className="px-4 pb-8">
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No version history is available.</p>
        ) : (
          /* `defaultValue={0}` because no revision is a "completed step" — the
             timeline is chronology here, not progress. */
          <Timeline defaultValue={0}>
            {entries.map((entry, index) =>
              entry.kind === "version" ? (
                <VersionItem
                  key={`${entry.version.resourceId}:${entry.version.version}`}
                  version={entry.version}
                  step={index + 1}
                  active={entry.version.version === active && !asOf}
                  isPast={Boolean(asOf)}
                  expanded={Boolean(expanded[entry.version.version])}
                  onToggle={() =>
                    setExpanded((previous) => ({
                      ...previous,
                      [entry.version.version]: !previous[entry.version.version],
                    }))
                  }
                />
              ) : (
                <ChangePointItem
                  key={`change:${entry.point.instant}`}
                  point={entry.point}
                  step={index + 1}
                  active={asOf === entry.point.instant}
                  expanded={Boolean(expanded[entry.point.instant])}
                  onToggle={() =>
                    setExpanded((previous) => ({
                      ...previous,
                      [entry.point.instant]: !previous[entry.point.instant],
                    }))
                  }
                />
              ),
            )}
          </Timeline>
        )}
      </div>
    </>
  );
}

/**
 * An instant at which something UNDER this definition was revised.
 *
 * It has no version of its own to show — that is the point of it — so it names
 * what moved instead. Clicking the marker resolves the page at that instant,
 * exactly like a version row, because both are the same kind of navigation:
 * pick a moment, see what it resolved to.
 */
function ChangePointItem({
  point,
  step,
  active,
  expanded,
  onToggle,
}: {
  point: ChangePoint;
  step: number;
  active: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const names = point.authored.map((entry) => entry.name ?? entry.lineage);
  const summary = names.length === 1 ? names[0] : `${names[0]} and ${names.length - 1} more`;

  return (
    <TimelineItem step={step} className="not-last:pb-6" aria-current={active ? "true" : undefined}>
      <TimelineSeparator className="bg-border group-data-[orientation=vertical]/timeline:h-[calc(100%-1.75rem)] group-data-[orientation=vertical]/timeline:translate-y-7" />
      <ChangePointIndicator instant={point.instant} active={active} label="View the catalog as it stood here" />

      <Collapsible open={expanded} onOpenChange={onToggle} className="flex flex-col">
        <CollapsibleTrigger className="-mx-1.5 flex cursor-pointer items-center justify-between gap-3 rounded-md px-1.5 py-1 text-left hover:bg-accent/50">
          <span className="flex min-w-0 items-center gap-2">
            <span className={cn("truncate text-sm", active ? "font-semibold" : "font-normal text-muted-foreground")}>
              {summary}
            </span>
            <span className="shrink-0 text-xs whitespace-nowrap text-muted-foreground/70">revised underneath</span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <time dateTime={point.instant} className="text-xs whitespace-nowrap text-muted-foreground">
              {formatDate(point.instant)}
            </time>
            <ChevronDown
              aria-hidden="true"
              className={cn("size-3.5 text-muted-foreground/60", !expanded && "-rotate-90")}
            />
          </span>
        </CollapsibleTrigger>

        <CollapsibleContent>
          {/* One block per lineage authored here. Several can share an instant —
              a seeding run writes many at once — and merging their changes into
              one list would lose which definition each belonged to. */}
          <div className="mt-2.5 flex flex-col gap-3.5">
            {point.authored.map((entry) => (
              <AuthoredChanges key={`${entry.lineage}:${entry.version}`} entry={entry} enabled={expanded} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </TimelineItem>
  );
}

/**
 * What one lineage's revision changed, fetched on expand.
 *
 * The diff is the version history's own — same function, same labels — so a
 * child's changes read exactly like the root's rather than in a second
 * vocabulary invented here.
 *
 * The one component in this shell that fetches, deliberately. The rule is that
 * routes fetch and views take props, and it holds everywhere else here — but a
 * route cannot hoist this one: what to ask for is decided by which row the
 * reader opened, and a graph's worth of diffs fetched eagerly would be a query
 * per lineage for rows nobody expands.
 */
function AuthoredChanges({ entry, enabled }: { entry: ChangePointEntry; enabled: boolean }) {
  const { changes, isLoading } = useRevisionChanges(entry.lineage, entry.version, enabled);
  const name = entry.name ?? entry.lineage;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-baseline gap-1.5">
        <span className="text-[13px] font-medium">{name}</span>
        <span className="text-[11px] text-muted-foreground/70">v{entry.version}</span>
      </div>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading changes…</p>
      ) : changes.length === 0 ? (
        <p className="text-xs text-muted-foreground">No authored changes detected</p>
      ) : (
        changes.map((change, index) => <ChangeRow key={`${change.label}:${change.type}:${index}`} change={change} />)
      )}
    </div>
  );
}

function ChangePointIndicator({ instant, active, label }: { instant: string; active: boolean; label: string }) {
  const className = cn(
    "flex h-7 w-5 items-center justify-center border-none bg-transparent",
    active ? "text-info" : "text-muted-foreground/40",
  );

  if (active) {
    return (
      <TimelineIndicator className={className}>
        <GitBranch className="size-3.5" />
      </TimelineIndicator>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <TimelineIndicator
            aria-hidden={false}
            className={cn(className, "cursor-pointer rounded-full hover:text-foreground")}
            render={
              <Link aria-label={label} to="." search={(previous) => ({ ...(previous as object), asOf: instant })} />
            }
          >
            <GitBranch className="size-3.5" />
          </TimelineIndicator>
        }
      />
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
      {(["added", "removed", "changed"] as const).map((type) => (
        <span key={type} className="flex items-center gap-1.5">
          <Marker type={type} className="size-3.5 text-[10px]" />
          {type}
        </span>
      ))}
    </div>
  );
}

function VersionItem({
  version,
  step,
  active,
  isPast,
  expanded,
  onToggle,
}: {
  version: DefinitionVersionHistoryEntry;
  step: number;
  active: boolean;
  isPast: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const Icon = version.current ? Target : isCreation(version) ? CirclePlus : History;

  return (
    <TimelineItem step={step} className="not-last:pb-6" aria-current={active ? "true" : undefined}>
      {/* Starts below the re-centred marker rather than at the default 18px, which
          was measured against an indicator sitting at the item's top edge. */}
      <TimelineSeparator className="bg-border group-data-[orientation=vertical]/timeline:h-[calc(100%-1.75rem)] group-data-[orientation=vertical]/timeline:translate-y-7" />
      {/* Blue marks the version being READ, not the newest one — on a past view
          the rail has to point at where you are, or it argues with the page. */}
      <VersionIndicator icon={Icon} version={version} active={active} isPast={isPast} />

      <Collapsible open={expanded} onOpenChange={onToggle} className="flex flex-col">
        <CollapsibleTrigger className="-mx-1.5 flex cursor-pointer items-center justify-between gap-3 rounded-md px-1.5 py-1 text-left hover:bg-accent/50">
          <span className="flex min-w-0 items-center gap-2">
            <span className={cn("text-sm tracking-tight", active ? "font-semibold" : "font-normal")}>
              Version {version.version}
            </span>
            {version.author && (
              <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-semibold text-foreground/70">
                <span aria-hidden="true">{initials(version.author)}</span>
                <span className="sr-only">{version.author}</span>
              </span>
            )}
            <span className="text-xs whitespace-nowrap text-muted-foreground">{changeSummary(version.changes)}</span>
            {version.current && (
              <Badge className="bg-info text-info-foreground h-[18px] px-1.5 text-[11px]">Latest</Badge>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {version.authoredAt && (
              <time dateTime={version.authoredAt} className="text-xs whitespace-nowrap text-muted-foreground">
                {formatDate(version.authoredAt)}
              </time>
            )}
            <ChevronDown
              aria-hidden="true"
              className={cn("size-3.5 text-muted-foreground/60", !expanded && "-rotate-90")}
            />
          </span>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-2.5 flex flex-col gap-3.5">
            <div className="flex flex-col gap-0.5">
              {version.changes.map((change, index) => (
                <ChangeRow key={`${change.label}:${change.type}:${index}`} change={change} />
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </TimelineItem>
  );
}

/**
 * The rail marker doubles as the version selector: clicking it moves the page to
 * that version. The active one is not a link — you are already there.
 *
 * The current version clears the key; a past one sets an INSTANT, never a
 * version number. Catalog edges are unpinned, so a parent's children move
 * underneath it — "version 3" names a revision of one definition, never a state
 * of the graph it sits in, and the as-of read takes an instant for that reason.
 */
function VersionIndicator({
  icon: Icon,
  version,
  active,
  isPast,
}: {
  icon: LucideIcon;
  version: DefinitionVersionHistoryEntry;
  active: boolean;
  isPast: boolean;
}) {
  // `h-7` is the trigger row's height (a text-sm line plus its `py-1`), not the
  // glyph's. The indicator is absolutely positioned at the item's top edge, so
  // matching the row's box is what centres the marker on the title line.
  const className = cn(
    "flex h-7 w-5 items-center justify-center border-none bg-transparent",
    active ? "text-info" : "text-muted-foreground/50",
  );

  // The current revision stays clickable while a past instant is being read
  // even though it is also the active version: on a never-revised lineage those
  // are the same row, and it is the only way back to the present.
  const isWhereYouAre = active && !(isPast && version.current);
  if (isWhereYouAre || (!version.current && !version.authoredAt)) {
    return (
      <TimelineIndicator className={className}>
        <Icon className="size-3.5" />
      </TimelineIndicator>
    );
  }

  const label = version.current
    ? "Back to the current version"
    : `View the catalog as it stood on ${formatDate(version.authoredAt ?? "")}`;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <TimelineIndicator
            aria-hidden={false}
            className={cn(className, "cursor-pointer rounded-full hover:text-foreground")}
            render={
              <Link
                aria-label={label}
                to="."
                search={(previous) => {
                  const { asOf: _cleared, ...rest } = previous as Record<string, unknown>;
                  return version.current ? rest : { ...rest, asOf: version.authoredAt };
                }}
              />
            }
          >
            <Icon className="size-3.5" />
          </TimelineIndicator>
        }
      />
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Whether the shell is rendering the newest revision. An unresolved lineage
 * reads as latest, so the badge does not show a number and then flip once the
 * live query lands.
 */
function showingLatest(shown: string | undefined, versions: readonly DefinitionVersionHistoryEntry[]): boolean {
  const latest = versions.find((entry) => entry.current)?.version;
  return shown === undefined || latest === undefined || latest === shown;
}

const MARKERS: Record<DefinitionRevisionChange["type"], { glyph: string; className: string }> = {
  added: { glyph: "+", className: "bg-success/12 text-success" },
  removed: { glyph: "−", className: "bg-destructive/12 text-destructive" },
  changed: { glyph: "~", className: "bg-muted text-muted-foreground" },
  created: { glyph: "★", className: "bg-muted text-muted-foreground" },
};

function Marker({ type, className }: { type: DefinitionRevisionChange["type"]; className?: string }) {
  const marker = MARKERS[type];
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-sm font-mono text-[11px] font-semibold",
        marker.className,
        className,
      )}
    >
      {marker.glyph}
    </span>
  );
}

function ChangeRow({ change }: { change: DefinitionRevisionChange }) {
  const path = change.label.split(" › ");
  const field = path.pop() ?? change.label;
  const context = path.join(" › ");

  return (
    <div className="-mx-2 flex gap-2.5 rounded-md px-2 py-1.5">
      <Marker type={change.type} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-1.5">
          <span className="text-[13px] font-medium">{field}</span>
          {context && <span className="text-[11px] text-muted-foreground/70">{context}</span>}
        </div>
        {(change.before || change.after) && (
          <div className="mt-1 flex flex-wrap items-baseline gap-1.5 font-mono text-xs leading-relaxed">
            {change.before && <span className="text-muted-foreground/70 break-words">{change.before}</span>}
            {change.before && change.after && (
              <span aria-hidden="true" className="shrink-0 text-muted-foreground/40">
                →
              </span>
            )}
            {change.after && <span className="break-words">{change.after}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function isCreation(version: DefinitionVersionHistoryEntry): boolean {
  return version.changes.some((change) => change.type === "created");
}

function changeSummary(changes: DefinitionVersionHistoryEntry["changes"]): string {
  if (changes.some((change) => change.type === "created")) return "Definition created";
  if (changes.length === 0) return "No authored changes detected";
  return `${changes.length} ${changes.length === 1 ? "change" : "changes"}`;
}

/** Two letters at most, so the avatar stays a circle whatever the name. */
function initials(author: string): string {
  return author
    .split(" ")
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export { VersionHistory };
